import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, EMPTY, expand, finalize, map, Observable, of, reduce, tap } from 'rxjs';

import { IdentityTeam, PagedResponse, TeamType } from '@reg/shared/model';
import { TeamSuggestion } from '@reg/identity/search/team-suggestion.model';
import { HttpServiceHelper } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save team data.
 */
@Injectable()
export class IdentityTeamService {
    public static INCLUDE_TEAM_TYPE: string = 'teamType';
    public static INCLUDE_ASSESSORS: string = 'assessors';
    public static INCLUDE_ORGANIZATIONS: string = 'organizations';
    public static INCLUDE_CONTACTS: string = 'contacts';
    public static PARAM_USER_PERMISSIONS_META: string = 'userPermissionsMeta';

    private readonly identityApiBaseUrl: string;
    private readonly identitySportName: string;
    private cachedTeamTypes: TeamType[] = null;
    private cachedTeamMap: { [apiRequest: string]: IdentityTeam; } = {};
    private cachedTeamsMap: { [apiRequest: string]: PagedResponse; } = {};
    private cachedUserTeamsMap: { [apiRequest: string]: IdentityTeam[]; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
        this.identitySportName = http.configService.getSportConfig('identitySportName');
    }

    /**
     * Get all teams for the specified event
     */
    public getIndexListResponse(
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<PagedResponse> {
        queryParams = this.appendSportId(queryParams);
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);
        return this.getIndexListResponseByUrl(apiRequestUrl);
    }

    /**
     * Get teams by a url. This is used for paging through teams since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(url: string, forceRefresh: boolean = false): Observable<PagedResponse> {
        if (!forceRefresh &&
            this.cachedTeamsMap[url]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedTeamsMap[url].clone());
        }
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const included: any[] = response.included;
                const json: any = response;

                const teams: IdentityTeam[] = [];
                for (const team of json.data) {
                    teams.push((new IdentityTeam()).deserialize(team, included, this.identitySportName));
                }

                const pagedResponse: PagedResponse = new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    teams,
                );

                this.cachedTeamsMap[url] = pagedResponse.clone();

                return pagedResponse;
            }));
    }

    /**
     * Get all teams the user has access to. If the user happens to have access to over the page size it will
     * auto fetch the next page and so on
     */
    public getUserTeams(includes: string[] = null, forceRefresh: boolean = false): Observable<IdentityTeam[]> {
        const queryParams: string[] = ['userGuidWithModifyAccess=current', 'page[size]=500'];
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);

        if (!forceRefresh && this.cachedUserTeamsMap[apiRequestUrl]) {
            // if `cachedTeams` is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedUserTeamsMap[apiRequestUrl].map((x: IdentityTeam) => x.clone()),
            );
        }
        // Make the http request
        // Recursively get all teams in this event
        return this.http.get(apiRequestUrl)
            .pipe(
                expand((response: IJsonApiResponse) => {
                    const json: Object = response;

                    if (json['links'] && json['links']['next']) {
                        // If you have more values we need to call http again. This is done recursively
                        return this.http.get(json['links']['next'], false, false);
                    }
                    return EMPTY;
                }),
                map((response: IJsonApiResponse) => {
                    const teams: IdentityTeam[] = [];
                    const included: any[] = response.included;

                    const bodyData: any = response.data || [];
                    for (const team of bodyData) {
                        teams.push((new IdentityTeam()).deserialize(team, included, this.identitySportName));
                    }

                    return teams;
                }),
                reduce((previousValue: any, currentValue: any) => previousValue.concat(currentValue)),
                tap((returnData: IdentityTeam[]) => {
                    this.cachedUserTeamsMap[apiRequestUrl] = returnData.map((x: IdentityTeam) => x.clone());
                    return this.cachedUserTeamsMap[apiRequestUrl];
                }),
            );
    }

    /**
     * search for teams
     */
    public search(searchText: string): Observable<TeamSuggestion[]> {
        if (!searchText) {
            return of([]);
        }

        const orParams: string[] = [];
        const andParams: string[] = [];

        const escapedSearchText: string = encodeURIComponent(searchText.trim().replace(/'/g, '\\\''));
        andParams.push('(term field=entity_name \'team\')');
        orParams.push(`(prefix field=name boost=10 '${escapedSearchText}')`);
        orParams.push(`(prefix field=location_city '${escapedSearchText}')`);
        const words: string[] = searchText.split(' ');

        // For each word add a "near" query
        words.forEach((word: string) => {
            const trimmedWord: string = encodeURIComponent(word.trim().replace(/'/g, '\\\''));
            if (trimmedWord) {
                encodeURIComponent(searchText.trim().replace(/'/g, '\\\''));
                orParams.push(`(near field=name distance=3 '${trimmedWord}*')`);
            }
        });

        let query: string = `(and ${andParams.join(' ')}`;

        if (orParams.length > 0) {
            query += `(or ${orParams.join(' ')})`;
        }

        query += ')';
        query += '&q.parser=structured';

        let apiRequestUrl: string = `${this.getApiUrl(null, null, null)}search`;
        apiRequestUrl += this.http.getQueryParameters(null, [`q=${query}`]);

        return this.http.get(apiRequestUrl, false)
            .pipe(map((response: IJsonApiResponse) => {
                const teamSuggestions: TeamSuggestion[] = [];

                const responseBody: any = response;
                if (responseBody?.hits?.hit) {
                    for (const hit of responseBody.hits.hit) {
                        teamSuggestions.push((new TeamSuggestion()).fillFromSearchHit(hit));
                    }
                }

                return teamSuggestions;
            }));
    }

    /**
     * Get all team types from identity
     */
    public getTeamTypes(forceRefresh: boolean = false): Observable<TeamType[]> {
        if (!forceRefresh &&
            this.cachedTeamTypes) {
            // if `cachedTeamTypes` is available just return it as `Observable`
            return observableInSeparateThread(this.cachedTeamTypes.map((x: TeamType) => x.clone()));
        }
        // Make the http request
        const apiUrl: string = `${this.identityApiBaseUrl}/team-types/`;

        return this.http.get(apiUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const types: TeamType[] = [];

                const bodyData: any = response.data || [];
                for (const type of bodyData) {
                    types.push((new TeamType()).deserialize(type));
                }
                this.cachedTeamTypes = types;
                return this.cachedTeamTypes.map((x: TeamType) => x.clone());
            }));
    }

    /**
     * Get a single team's data
     */
    public get(
        identityTeamId: string,
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<IdentityTeam> {
        if (!identityTeamId) {
            return of(null);
        }

        queryParams = queryParams || [];

        if (queryParams.filter(
            (x: string) => x.indexOf(IdentityTeamService.PARAM_USER_PERMISSIONS_META) !== -1,
        ).length === 0) {
            queryParams.push(`${IdentityTeamService.PARAM_USER_PERMISSIONS_META}=1`);
        }

        const apiRequestUrl: string = this.getApiUrl(identityTeamId, includes, queryParams);

        if (!forceRefresh &&
            this.cachedTeamMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedTeamMap[apiRequestUrl].clone());
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const identityTeam: IdentityTeam =
                        (new IdentityTeam().deserialize(response, null, this.identitySportName));
                this.cachedTeamMap[apiRequestUrl] = identityTeam.clone();
                return identityTeam;
            }));
    }

    /**
     * Get a single team's data
     */
    public getByIds(identityTeamIds: string[], includes: string[] = null, queryParams: string[] = null)
    : Observable<IdentityTeam[]> {
        queryParams = queryParams || [];

        if (queryParams.filter(
            (x: string) => x.indexOf(IdentityTeamService.PARAM_USER_PERMISSIONS_META) !== -1,
        ).length === 0) {
            queryParams.push(`${IdentityTeamService.PARAM_USER_PERMISSIONS_META}=1`);
        }

        const apiRequestUrl: string = this.getApiUrl('get-by-ids', includes, queryParams);

        // Make the http request
        return this.http.post(apiRequestUrl, { teamIds: identityTeamIds })
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;

                return json.data.map(
                    (team: any) => new IdentityTeam().deserialize(team, json.included, this.identitySportName),
                );
            }));
    }

    /**
     * Check if the team an modify results
     */
    public canModifyResults(identityTeamId: string, eventId: string): Observable<ICanModifyResult> {
        const queryParams: string[] = [`eventId=${eventId}`];
        const apiRequestUrl: string = this.getApiUrl(`${identityTeamId}/can-modify-results`, null, queryParams);

        // Make the http request
        return this.http.get<ICanModifyResult>(apiRequestUrl)
            .pipe(map((response: ICanModifyResult) => response));
    }

    /**
     * Add or update the team
     */
    public save(team: IdentityTeam, includes: string[] = null): Observable<IdentityTeam> {
        // Clear cached results
        this.clearCache();

        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(team.id, includes);

        if (team.id) {
            // Update existing Team
            response = this.http.patch(apiRequestUrl, team.serialize());
        } else {
            // Add a new Team
            response = this.http.post(apiRequestUrl, team.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityTeam())
                    .deserialize(response, null, this.identitySportName)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Add one or more athletes to the given identity team
     *
     * @param {string} identityTeamId The identity team id
     * @param {string[]} identityPersonIds The list of people to link to the given team
     * @param {boolean} addingAfterAthleteAdd Flag used to determine if this method is being called after adding an athlete
     * @returns {Observable<IdentityTeam>}
     */
    public addAthletes(
        identityTeamId: string,
        identityPersonIds: string[],
        addingAfterAthleteAdd: boolean = false,
    ): Observable<IdentityTeam> {
        let apiRequestUrl: string = this.getApiUrl(identityTeamId, null, null);
        apiRequestUrl += '/relationships/athletes';

        const json: any = {};
        json.data = [];

        identityPersonIds.forEach((identityPersonId: string) => {
            json.data.push({ type: 'person', id: identityPersonId });
        });

        return this.http.post(apiRequestUrl, JSON.stringify(json), true, false)
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityTeam())
                    .deserialize(response, null, this.identitySportName)),
                catchError((error: HttpErrorResponse) => {
                    if (addingAfterAthleteAdd) {
                        // Don't display a toast message if we failed to save to identity, just fail silently.
                        // This can happen when trying to add an athlete to a team when we don't know if the person
                        // has access to the team before trying.
                        throw (error);
                    }

                    return this.http.handleError(error, this.http.toastService);
                }),
            );
    }

    public deleteAthletes(identityTeamId: string, identityPersonIds: string[]): Observable<IdentityTeam> {
        let apiRequestUrl: string = this.getApiUrl(identityTeamId, null, null);
        apiRequestUrl += '/relationships/athletes';

        const json: any = {};
        json.data = [];
        identityPersonIds.forEach((identityPersonId: string) => {
            json.data.push({ type: 'person', id: identityPersonId });
        });

        return this.http.delete(apiRequestUrl, true, false, this.http.getHeadersForJson(), JSON.stringify(json))
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityTeam())
                    .deserialize(response, null, this.identitySportName)),
                catchError((error: HttpErrorResponse) =>
                    // Don't display a toast message if we failed to save to identity, just fail silently
                    this.http.handleError(error, null)),
            );
    }

    public clearCache(): void {
        this.cachedTeamMap = {};
        this.cachedTeamsMap = {};
        this.cachedUserTeamsMap = {};
    }

    private appendSportId(queryParams: string[]): string[] {
        queryParams = queryParams || [];

        // Append the sportId query param if it doesn't already exist
        if (queryParams.filter((x: string) => x.indexOf('sportId=') === -1).length === 0) {
            queryParams.push(`sportId=${this.http.configService.getSportConfig('identitySportGuid')}`);
        }

        return queryParams;
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(teamId: string, includes: string[], queryParams: string[] = null): string {
        let url: string = `${this.identityApiBaseUrl}/teams/`;

        if (teamId) {
            url += teamId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

export interface ICanModifyResult {
    canModifyResults: boolean;
    reason: string;
}
